import { useMemo } from "react";
import { Link } from "react-router";
import { getShark, listSharks } from "@/api/client";
import type { Constellation, Shark } from "@/api/types";
import { useAsync } from "@/hooks/useAsync";
import { grothTriangle } from "@/site/geometry";
import { to } from "@/site/parts";
import { Plate } from "@/site/plate";

/** The most-observed shark in the registry: its constellation is the specimen on the bench. */
export function useReferenceShark() {
  return useAsync("reference", async (signal) => {
    const page = await listSharks({ sort: "encounters", limit: 1 }, signal);
    const first = page.items[0];
    if (!first) throw new Error("The registry is empty: no reference shark to plot.");
    return getShark(first.id, signal);
  });
}

/** The reference specimen's constellation with one Groth triangle and its invariants. */
export function ReferenceFigure({ shark }: { shark: Shark | undefined }) {
  const c: Constellation | null | undefined = shark?.constellation;
  const tri = useMemo(() => (c ? grothTriangle(c) : null), [c]);
  if (!shark || !c) {
    return <div className="plate plate-chart" style={{ aspectRatio: "4 / 3" }} aria-hidden />;
  }
  return (
    <figure style={{ margin: 0 }}>
      <div className="fig-head">
        <span className="label">Fig. A · Reference specimen</span>
        <Link to={to.shark(shark.id)} className="label link-u" style={{ color: "var(--ink)" }}>
          {shark.id} →
        </Link>
      </div>
      <Plate
        imageUrl={null}
        constellation={c}
        aspect={4 / 3}
        edges
        triangle={tri?.vertices ?? null}
        label={`Spot constellation of ${shark.display_name}: ${c.spots.length} spots, one Groth triangle marked`}
      />
      <figcaption className="fig-cap">
        <span className="label">
          {c.spots.length} spots · ENC {shark.encounters}
        </span>
        {tri && (
          <span className="label">
            △ABC · R = {tri.ratio.toFixed(2)} · C = {tri.cosine.toFixed(2)}
          </span>
        )}
      </figcaption>
      <p className="muted" style={{ fontSize: 13, marginTop: 10, maxWidth: "34em" }}>
        {shark.display_name}, the most-observed individual on record. Every trio of spots forms a
        triangle; its side ratio R and angle cosine C survive scale and rotation, so they can be
        matched across photographs.
      </p>
    </figure>
  );
}
